// EXEMPT
import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { touchOnly } from "clutch/src/Style/style.mjs";
import { Tag } from "clutch/src/Tag/Tag.js";

import { GamesDropdownWithWrapper } from "@/app/AppShellGamesDropdown.jsx";
import GamesProfilesDropdown from "@/app/AppShellGamesProfilesDropdown.jsx";
import MegaMenu from "@/app/AppShellMegaMenu.jsx";
import { UserDropdown } from "@/app/AppShellUserDropdown.jsx";
import type { GameNavItem } from "@/app/constants-nav.mjs";
import { getGameDefinition } from "@/app/games.mjs";
import UpdateAvailableButton from "@/app/UpdateAvailableButton.jsx";
// import DiscordIcon from "@/inline-assets/discord-logo.svg";
import SettingsGear from "@/inline-assets/gear.svg";
import { useCurrentGame } from "@/util/game-route.mjs";
import isMobile from "@/util/is-mobile.mjs";
import { useRoute } from "@/util/router-hooks.mjs";
import { sendInteractionEvent } from "@/util/use-interaction-event.mjs";

const GameLinksBarWrapper = styled("div")`
  & {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    align-self: stretch;
    height: var(--secondary-nav-height);
    width: 100%;
    max-width: calc(
      var(--header-container, var(--sp-container)) + var(--page-padding) * 2
    );
    padding-inline: var(--page-padding);
    margin-inline: auto;
  }

  .left-nav {
    display: flex;
    align-items: center;
    gap: var(--sp-4);
  }
  .game-links {
    display: flex;
  }

  .right-nav {
    display: flex;
    align-items: center;
    gap: var(--sp-1);

    @container app (width <= 767px) {
      display: none;
    }

    .button {
      padding: var(--sp-1);
      border-radius: 50%;

      svg {
        width: var(--sp-5);
        height: var(--sp-5);
      }
    }
  }

  .active-border {
    position: absolute;
    bottom: 0;
    left: 50%;
    translate: -50% 0;
    width: var(--sp-6);
    height: var(--br-sm);
    border-radius: var(--br-sm) var(--br-sm) 0 0;
    background: var(--game-accent-color);
  }

  ${touchOnly} {
    overflow-x: auto;
    overflow-y: hidden;

    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
    /* Safari and Chrome */
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const GameLink = styled("div")`
  position: relative;
  color: var(--shade1);
  white-space: nowrap;
  transition: color var(--transition);

  &:hover {
    color: var(--shade0);
    z-index: 1;

    .mega-menu {
      display: flex;
    }
  }

  > a {
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--secondary-nav-height);
    padding-inline: var(--sp-2_5);

    @container main-nav (inline-size <= 1000px) {
      padding-inline: var(--sp-1_5);
    }
  }

  .mega-menu {
    display: none;
    position: absolute;
    top: calc(100% - var(--sp-1));
    left: 0px;
    box-shadow:
      0px 3px 3px -35px rgba(0, 0, 0, 0.12),
      0px 6px 8px -35px rgba(0, 0, 0, 0.27),
      0px 10px 15px -35px rgba(0, 0, 0, 0.33),
      0px 12px 25px -35px rgba(0, 0, 0, 0.39),
      0px 20px 41px -35px rgba(0, 0, 0, 0.45),
      0px 40px 72px -35px rgba(0, 0, 0, 0.54),
      0px 80px 156px -35px rgba(0, 0, 0, 0.73);
  }

  a.active {
    color: var(--shade0);
  }
`;

const Button = styled("a")`
  color: var(--shade2);
`;

const isActive = ({ currentPath, href }) => {
  const urfRegex = new RegExp(/\/lol\/(champions|tierlist)\/urf/);
  if (currentPath.match(urfRegex)) {
    return href.match(urfRegex);
  }
  return currentPath.startsWith(href);
};

const MainNavGameLinksBar = () => {
  const { t } = useTranslation();
  const gameSymbol = useCurrentGame();
  const links = getGameDefinition(gameSymbol)?.navLinks;

  return (
    <GameLinksBarWrapper>
      <div className="left-nav">
        <GamesDropdownWithWrapper />
        <GamesProfilesDropdown />
        <nav className="game-links">
          {links?.map((item, i) => <MainNavGameLink key={i} {...item} />)}
        </nav>
      </div>
      <div className="right-nav">
        <UpdateAvailableButton />
        {/* <Button
          href="https://discord.gg/theblitzapp"
          className="button"
          data-tip={t("common:discord", "Discord")}
          data-place="bottom"
          onClick={() => {
            sendInteractionEvent("app-navigation-discord-button");
          }}
        >
          <DiscordIcon />
        </Button> */}
        <Button
          href="/settings"
          className="button"
          data-tip={t("common:settings", "Settings")}
          data-place="bottom"
          onClick={() => {
            sendInteractionEvent("app-navigation-settings-button");
          }}
        >
          <SettingsGear />
        </Button>
        <UserDropdown />
      </div>
    </GameLinksBarWrapper>
  );
};

const MainNavGameLink = (item: GameNavItem) => {
  const { t } = useTranslation();
  const { currentPath } = useRoute({ isGlobal: true });

  const active = isActive({
    currentPath,
    href: item.href,
  });

  return (
    <GameLink key={item.href}>
      <a
        href={item.href}
        className={`type-form--button flex gap-1${active ? " active" : ""}`}
        onClick={() => {
          sendInteractionEvent("app-navigation-game-menu-link", {
            url: item.href,
          });
        }}
      >
        <span>{t(...item.text)}</span>
        {item.tag && (
          <Tag text={t(...item.tag)} size="xs" color="var(--shade2)" />
        )}
      </a>
      {active && <div className="active-border" />}
      {item.menu && !isMobile() && (
        <MegaMenu className="mega-menu" menu={item.menu} />
      )}
    </GameLink>
  );
};

export default React.memo(MainNavGameLinksBar);
